import { useState, useEffect, useCallback, useRef } from 'react';
import { routes, pathFor } from './config/routes';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { usePageMeta } from './hooks/usePageMeta';
import { TurfAIPage } from './components/TurfAIPage';
import { PlatformPage } from './components/PlatformPage';
import { PlatformTopicPage } from './components/PlatformTopicPage';
import { platformTopics } from './data/platform';
import { SolutionsPage } from './components/SolutionsPage';
import { IndustrySolutionPage } from './components/IndustrySolutionPage';
import { industries } from './data/solutions';
import { ResourcesPage } from './components/ResourcesPage';
import { AcceleratorPage } from './components/AcceleratorPage';
import { CaseStudyPage } from './components/CaseStudyPage';
import { accelerators } from './data/accelerators';
import { caseStudies } from './data/caseStudies';
// SmartList - commented out temporarily
import { ScheduleDemoPage } from './components/ScheduleDemoPage';
import { ContactSupportPage } from './components/ContactSupportPage';
import { AboutPage } from './components/AboutPage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { SecurityPage } from './components/SecurityPage';
import { NotFoundPage } from './components/NotFoundPage';
// Get page from current URL path, redirecting duplicates to canonical paths
function getPageFromPath(): string {
  // Normalize: strip trailing slash (except root "/")
  const raw = window.location.pathname;
  const path = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;

  // Redirect /turfai to / to avoid duplicate content
  if (path === '/turfai') {
    window.history.replaceState(null, '', '/');
    return 'turfai';
  }

  // Clean up URL if trailing slash was stripped
  if (path !== raw) {
    window.history.replaceState(null, '', path);
  }

  const page = routes[path];
  if (!page) return 'not-found';
  // Old or duplicate URLs (e.g. /ask-hr) land on the page's canonical path.
  const canonical = pathFor(page);
  if (canonical !== path && path !== '/turfai') window.history.replaceState(null, '', canonical);
  return page;
}

export default function App() {
  const [currentPage, setCurrentPage] = useState(getPageFromPath);
  const [scrollToSection, setScrollToSection] = useState<string | null>(null);

  // Update canonical URL, title, and meta description per page
  usePageMeta(currentPage);

  // Handle navigation with URL update
  const handleNavigate = useCallback((page: string) => {
    const path = pathFor(page);
    window.history.pushState({ page }, '', path);
    setCurrentPage(page);
    setScrollToSection(null);
  }, []);

  const handleNavigateToDemo = useCallback(() => {
    const path = pathFor('schedule-demo');
    window.history.pushState({ page: 'schedule-demo' }, '', path);
    setCurrentPage('schedule-demo');
    setScrollToSection('demo-form');
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromPath());
      setScrollToSection(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Disable browser scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  // Scroll to top whenever page changes
  useEffect(() => {
    // Immediate scroll to top without smooth animation to prevent timing issues
    window.scrollTo(0, 0);
    
    // Additional check with a small delay to ensure page content is rendered
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
    
    return () => clearTimeout(timer);
  }, [currentPage]);

  // After a client-side page change, move focus to the new content so keyboard and
  // screen-reader users land on it (skipped on first load, where the browser handles focus).
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    mainRef.current?.focus({ preventScroll: true });
  }, [currentPage]);

  // Handle scrolling to section after page navigation
  useEffect(() => {
    if (scrollToSection) {
      const timer = setTimeout(() => {
        const element = document.getElementById(scrollToSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setScrollToSection(null);
      }, 100); // Small delay to ensure page has rendered
      
      return () => clearTimeout(timer);
    }
  }, [currentPage, scrollToSection]);

  // Enhanced navigation function that passes both regular navigate and demo navigate
  const getNavigationProps = () => ({
    onNavigate: handleNavigate,
    onNavigateToDemo: handleNavigateToDemo
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <a href="#main" className="skip-link">Skip to content</a>
      <Navigation currentPage={currentPage} onNavigate={handleNavigate} onNavigateToDemo={handleNavigateToDemo} />
      
      <main id="main" ref={mainRef} tabIndex={-1} className="flex-1 outline-none">
        {currentPage === 'turfai' && <TurfAIPage {...getNavigationProps()} />}
        {currentPage === 'platform' && <PlatformPage {...getNavigationProps()} />}
        {platformTopics.map((t) =>
          currentPage === t.pageId ? (
            <PlatformTopicPage key={t.pageId} data={t} {...getNavigationProps()} />
          ) : null
        )}
        {currentPage === 'solutions' && <SolutionsPage {...getNavigationProps()} />}
        {industries.map((ind) =>
          currentPage === ind.pageId ? (
            <IndustrySolutionPage key={ind.pageId} data={ind} {...getNavigationProps()} />
          ) : null
        )}
        {currentPage === 'resources' && <ResourcesPage {...getNavigationProps()} />}
        {accelerators.map((a) =>
          currentPage === a.pageId ? <AcceleratorPage key={a.pageId} data={a} {...getNavigationProps()} /> : null
        )}
        {caseStudies.map((c) =>
          currentPage === c.pageId ? <CaseStudyPage key={c.pageId} data={c} {...getNavigationProps()} /> : null
        )}
        {/* SmartList - commented out temporarily */}
        {currentPage === 'schedule-demo' && <ScheduleDemoPage {...getNavigationProps()} />}
        {currentPage === 'contact-support' && <ContactSupportPage {...getNavigationProps()} />}
        {currentPage === 'about' && <AboutPage {...getNavigationProps()} />}
        {currentPage === 'privacy-policy' && <PrivacyPolicyPage {...getNavigationProps()} />}
        {currentPage === 'terms-of-service' && <TermsOfServicePage {...getNavigationProps()} />}
        {currentPage === 'security' && <SecurityPage {...getNavigationProps()} />}
        {currentPage === 'not-found' && <NotFoundPage onNavigate={handleNavigate} />}
      </main>

      <Footer onNavigate={handleNavigate} onNavigateToDemo={handleNavigateToDemo} />
    </div>
  );
}